import * as React from 'react';
import type { ComposerState } from '@/types/api';
import { ApiError } from '@/lib/api';
import { COMPOSER_REASON, errorMessage } from '@/lib/copy';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { StateChip } from '@/components/state-chip';
import { HiddenGlyph } from '@/components/visibility-band';
import { toast } from '@/components/ui/toast';
import { usePostExpertMessage } from './queries';

/* ---------------------------------------------------------------------------
   THE EXPERT COMPOSER.

   It sits inside the hatched band, permanently. Everything typed here is
   inserted at `visibility: expert` and is never visible to the client, and the
   composer is where a reviewer is most likely to forget that — so the band and
   the stamp are not decoration on this control, they are the control's most
   important label.

   THE GREYED STATE COMES FROM THE SERVER. `composer.can_prompt` and
   `composer.reason_code` are produced by `CanPrompt(chat, actor)`, the same
   predicate that fills the 409 body — so the greyed reason and the rejection
   cannot disagree, and nothing here re-derives either from the status. Each
   reason code gets its own sentence: a generic "you can't send this right now"
   throws away the one piece of information the predicate exists to carry.

   `client_message_id` is generated per composed message and reused across
   retries of the same text. A double-tap on a slow connection returns the
   original 202 rather than starting a second run on the project.
--------------------------------------------------------------------------- */

function newMessageId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `expert-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export function Composer({
  chatId,
  composer,
  className,
}: {
  chatId: number;
  composer: ComposerState | null;
  className?: string;
}) {
  const [text, setText] = React.useState('');
  const idRef = React.useRef<string>(newMessageId());
  const post = usePostExpertMessage(chatId);

  const reason = composer ? COMPOSER_REASON[composer.reason_code] : null;
  const blocked = composer ? !composer.can_prompt : true;
  const canSend = !blocked && text.trim().length > 0 && !post.isPending;

  const send = () => {
    if (!canSend) return;
    post.mutate(
      { text: text.trim(), client_message_id: idRef.current },
      {
        onSuccess: (accepted) => {
          setText('');
          idRef.current = newMessageId();
          if (accepted.idempotent_replay) {
            toast('info', 'Already sent', 'That message had already started a run. No second run was created.');
          }
        },
        onError: (error) => {
          if (!(error instanceof ApiError)) return;
          const code = error.reasonCode;
          toast(
            'error',
            'The message was refused',
            code ? COMPOSER_REASON[code].detail ?? error.message : errorMessage(error.code, error.message),
          );
        },
      },
    );
  };

  return (
    <div className={cn('relative pl-3', className)} data-visibility="expert">
      <span
        aria-hidden
        className="hatch-hidden pointer-events-none absolute bottom-0 left-0 top-0 w-[3px] rounded-[1px]"
      />

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="flex items-center gap-1.5 font-mono text-tiny uppercase tracking-[0.1em] text-hidden">
          <HiddenGlyph />
          Hidden from client
        </p>
        {reason ? <StateChip size="sm" state={reason} /> : null}
      </div>

      <Textarea
        rows={3}
        value={text}
        disabled={blocked || post.isPending}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          // Enter sends; the modifier inserts a newline. A reviewer's prompt is
          // usually one line, and a Send button that needs the mouse breaks the
          // rhythm of a review.
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            send();
          }
        }}
        placeholder={blocked ? 'The composer is closed' : 'Prompt the agent — the client never sees this'}
        className="mt-1.5 font-normal"
      />

      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
        <p className="max-w-prose text-xs leading-[17px] text-ink-3">
          {blocked
            ? (reason?.detail ?? composer?.message ?? 'The server has closed the composer for this chat.')
            : 'Your turn and the run it starts are inserted at expert visibility. An expert-actor failure returns this chat to pending review, never to draft.'}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <span className="font-mono text-tiny text-ink-3">⏎ send · ⇧⏎ newline</span>
          <Button variant="hidden" disabled={!canSend} loading={post.isPending} onClick={send}>
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
